import React, { useEffect, useMemo, useState } from "react";
import { Shield, Search } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  "customer", "affiliate", "driver", "partner", "franchisee",
  "trainer", "teacher", "affiliate_trainer", "franchisee_trainer",
  "training_admin", "admin", "master_admin",
];

export default function AdminRoleHub() {
  const { user } = useAuth();
  const isMaster = (user?.roles || []).includes("master_admin");
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.roles || []).join(" ").toLowerCase().includes(q)
    );
  }, [users, filter]);

  const setRoles = async (uid, roles) => {
    setBusyId(uid); setErr("");
    try {
      await api.put(`/admin/users/${uid}/roles`, { roles });
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusyId(null); }
  };

  const toggleRole = (u, role) => {
    if (!isMaster) return;
    const has = (u.roles || []).includes(role);
    let next = has ? u.roles.filter((r) => r !== role) : [...u.roles, role];
    // Customer is the floor — keep it
    if (!next.includes("customer")) next = ["customer", ...next];
    setRoles(u.id, next);
  };

  return (
    <div data-testid="admin-role-hub" className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow flex items-center gap-2"><Shield className="h-3 w-3" /> Identity</div>
        <h1 className="headline-xl">Role hub</h1>
        <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
          Multi-role identity console. {isMaster
            ? <span className="text-lifted-green">You are master_admin — click any role chip to toggle.</span>
            : <span className="text-rose-300" data-testid="role-hub-readonly-banner">Read-only — only master_admin may mutate roles.</span>}
        </p>
      </header>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="h-3 w-3 text-lifted-mute absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="search email / name / role…"
                 className="input-lifted text-sm pl-8" data-testid="role-hub-search" />
        </div>
        <span className="text-xs text-lifted-mute">{filtered.length} / {users.length} users</span>
      </div>

      {err && <div className="text-rose-300 text-sm" data-testid="role-hub-error">{err}</div>}

      <section className="glass-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="px-4 py-3">User</th><th>Roles</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((u) => (
              <tr key={u.id} data-testid={`role-hub-user-${u.id}`} className="border-b border-white/5">
                <td className="px-4 py-3 align-top w-72">
                  <div className="text-lifted-text">{u.name || "—"}</div>
                  <div className="text-xs text-lifted-mute">{u.email}</div>
                  <div className="text-[0.65rem] text-lifted-dim mt-1">status: {u.status}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {ROLES.map((r) => {
                      const on = (u.roles || []).includes(r);
                      return (
                        <button key={r} disabled={!isMaster || busyId === u.id}
                                onClick={() => toggleRole(u, r)}
                                data-testid={`role-hub-toggle-${u.id}-${r}`}
                                className={`chip ${on ? "chip--green" : "chip--purple opacity-30"} ${isMaster ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}>
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={2} className="px-4 py-10 text-center text-lifted-mute">No matches.</td></tr>}
          </tbody>
        </table>
      </section>
      {filtered.length > 200 && <div className="text-xs text-lifted-mute">Showing first 200 — refine the search to drill deeper.</div>}
    </div>
  );
}
